import React from 'react';
import { format } from 'date-fns';
import { ShoppingBagIcon, UserIcon, CurrencyDollarIcon, GiftIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';

// Printable component
const PrintableOrder = React.forwardRef(({ order, formattedDate, regularItems, giftItems, regularTotal, giftTotal }, ref) => (
  <div ref={ref} className="p-6 bg-white">
    <div className="text-center mb-6">
      <h1 className="text-2xl font-bold text-gray-900">Order #{order.orderNumber}</h1>
      <p className="text-sm text-gray-500">
        {formattedDate} • {order.customer?.name || 'N/A'}
      </p>
    </div>

    <div className="grid grid-cols-2 gap-6 mb-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">Customer Information</h2>
        <p className="text-gray-700">{order.customer?.name || 'N/A'}</p>
        {order.customer?.email && <p className="text-gray-600">{order.customer.email}</p>}
        {order.customer?.mobile && <p className="text-gray-600">{order.customer.mobile}</p>}
      </div>
      <div>
        <h2 className="text-lg font-semibold mb-2">Order Information</h2>
        <p className="text-gray-700">
          <span className="font-medium">Status:</span> {order.status || 'pending'}
        </p>
        <p className="text-gray-700">
          <span className="font-medium">Total:</span> {order.currency || 'BHD'} {regularTotal.toFixed(3)}
        </p>
        <p className="text-gray-700">
          <span className="font-medium">Payment:</span> {order.payment?.status === 'paid' || order.paymentMethod === 'cash' ? 'Paid' : 'Pending'}
          {order.payment?.method && ` (${order.payment.method})`}
        </p>
      </div>
    </div>

    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-3">Order Items</h2>
      <table className="min-w-full border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-4">Item</th>
            <th className="text-right py-2 px-4">Qty</th>
            <th className="text-right py-2 px-4">Price</th>
            <th className="text-right py-2 px-4">Total</th>
          </tr>
        </thead>
        <tbody>
          {regularItems.map((item, index) => (
            <tr key={index} className="border-b border-gray-100">
              <td className="py-3 px-4">{item.name}</td>
              <td className="text-right py-3 px-4">{item.quantity || 1}</td>
              <td className="text-right py-3 px-4">
                {item.unit_cost_local_currency ? parseFloat(item.unit_cost_local_currency).toFixed(3) : '0.000'}
              </td>
              <td className="text-right py-3 px-4">
                {item.unit_cost_local_currency ? (parseFloat(item.unit_cost_local_currency) * (item.quantity || 1)).toFixed(3) : '0.000'}
              </td>
            </tr>
          ))}
          {giftItems.map((item, index) => (
            <tr key={`gift-${index}`} className="border-b border-gray-100">
              <td className="py-3 px-4">{item.name} (Gift)</td>
              <td className="text-right py-3 px-4">{item.quantity || 1}</td>
              <td className="text-right py-3 px-4">Free</td>
              <td className="text-right py-3 px-4">Free</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="text-right mb-6">
      <div className="inline-block text-right">
        <div className="flex justify-between py-2">
          <span className="font-medium mr-4">Subtotal:</span>
          <span>{order.currency || 'BHD'} {regularTotal.toFixed(3)}</span>
        </div>
        {giftItems.length > 0 && (
          <div className="flex justify-between py-2">
            <span className="font-medium mr-4">Gifts:</span>
            <span>Free</span>
          </div>
        )}
        <div className="border-t border-gray-200 my-2"></div>
        <div className="flex justify-between py-2 font-bold">
          <span className="font-bold mr-4">Total:</span>
          <span>{order.currency || 'BHD'} {regularTotal.toFixed(3)}</span>
        </div>
      </div>
    </div>
  </div>
));

export default function OrderDetails({ order }) {
  const printRef = useRef();
  
  if (!order) return null;

  // Use created_at field from Firestore which is a Firestore Timestamp
  const date = order.created_at?.toDate ? order.created_at.toDate() : 
             order.timestamp ? new Date(order.timestamp) : new Date();
  const formattedDate = format(date, 'MMM d, yyyy h:mm a');
  
  // Calculate totals
  const regularItems = order.items?.filter(item => item.is_gift !== 'true') || [];
  const giftItems = order.items?.filter(item => item.is_gift === 'true') || [];
  
  const regularTotal = regularItems.reduce((sum, item) => {
    const price = parseFloat(item.unit_cost_local_currency || 0);
    const quantity = parseInt(item.quantity || 1, 10);
    return sum + (price * quantity);
  }, 0);
  
  const giftTotal = 0; // Gift items are free

  return (
    <div className="space-y-6">
      {/* Order Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Order #{order.orderNumber}</h2>
            <p className="text-sm text-gray-500">Placed on {formattedDate}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                const printWindow = window.open('', '_blank');
                const printContent = `
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <title>Order #${order.orderNumber || ''}</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        h1 { color: #111827; font-size: 24px; margin-bottom: 5px; }
                        h2 { color: #374151; font-size: 18px; margin: 20px 0 10px; }
                        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                        th, td { text-align: left; padding: 8px; border-bottom: 1px solid #e5e7eb; }
                        th { background-color: #f9fafb; }
                        .text-right { text-align: right; }
                        .text-center { text-align: center; }
                        .mt-4 { margin-top: 16px; }
                        .pt-4 { padding-top: 16px; }
                        .border-t { border-top: 1px solid #e5e7eb; }
                        .font-bold { font-weight: 600; }
                        .mr-4 { margin-right: 1rem; }
                      </style>
                    </head>
                    <body>
                      <div class="text-center">
                        <h1>Order #${order.orderNumber || 'N/A'}</h1>
                        <p>Placed on ${formattedDate}</p>
                      </div>

                      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0;">
                        <div>
                          <h2>Customer Information</h2>
                          <p><strong>Name:</strong> ${order.customer?.name || 'N/A'}</p>
                          ${order.customer?.email ? `<p><strong>Email:</strong> ${order.customer.email}</p>` : ''}
                          ${order.customer?.phone ? `<p><strong>Phone:</strong> +${order.customer.phone}</p>` : ''}
                        </div>
                        <div>
                          <h2>Order Information</h2>
                          <p><strong>Status:</strong> ${order.status || 'pending'}</p>
                          <p><strong>Total:</strong> ${order.currency || 'BHD'} ${regularTotal.toFixed(3)}</p>
                          <p><strong>Payment:</strong> ${order.payment?.method || (order.paymentMethod === 'cash' ? 'Cash' : 'Online Payment')}</p>
                        </div>
                      </div>

                      <h2>Order Items</h2>
                      <table>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th class="text-right">Qty</th>
                            <th class="text-right">Price</th>
                            <th class="text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${regularItems.map(item => `
                            <tr>
                              <td>${item.name}</td>
                              <td class="text-right">${item.quantity || 1}</td>
                              <td class="text-right">${item.unit_cost_local_currency ? parseFloat(item.unit_cost_local_currency).toFixed(3) : '0.000'}</td>
                              <td class="text-right">
                                ${item.unit_cost_local_currency ? (parseFloat(item.unit_cost_local_currency) * (item.quantity || 1)).toFixed(3) : '0.000'}
                              </td>
                            </tr>
                          `).join('')}
                          ${giftItems.map(item => `
                            <tr>
                              <td>${item.name} (Gift)</td>
                              <td class="text-right">${item.quantity || 1}</td>
                              <td class="text-right">Free</td>
                              <td class="text-right">Free</td>
                            </tr>
                          `).join('')}
                        </tbody>
                      </table>

                      <div class="text-right" style="margin-top: 20px;">
                        <div style="display: inline-block; text-align: right;">
                          <div style="display: flex; justify-content: space-between; padding: 8px 0;">
                            <span style="font-weight: 500; margin-right: 1rem;">Subtotal:</span>
                            <span>${order.currency || 'BHD'} ${regularTotal.toFixed(3)}</span>
                          </div>
                          ${giftItems.length > 0 ? `
                            <div style="display: flex; justify-content: space-between; padding: 8px 0;">
                              <span style="font-weight: 500; margin-right: 1rem;">Gifts:</span>
                              <span>Free</span>
                            </div>
                          ` : ''}
                          <div style="border-top: 1px solid #e5e7eb; margin: 8px 0;"></div>
                          <div style="display: flex; justify-content: space-between; padding: 8px 0; font-weight: 600;">
                            <span style="margin-right: 1rem;">Total:</span>
                            <span>${order.currency || 'BHD'} ${regularTotal.toFixed(3)}</span>
                          </div>
                        </div>
                      </div>

                      <div style="margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 14px; color: #6b7280;">
                        <p>Thank you for your business!</p>
                        <p style="margin-top: 4px;">Order placed on ${formattedDate}</p>
                      </div>
                    </body>
                  </html>
                `;
                
                printWindow.document.open();
                printWindow.document.write(printContent);
                printWindow.document.close();
                
                // Give it a moment to load before printing
                setTimeout(() => {
                  printWindow.print();
                  // Close after a short delay
                  setTimeout(() => printWindow.close(), 500);
                }, 500);
              }}
              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-full text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Print Order"
            >
              <PrinterIcon className="h-3.5 w-3.5 mr-1" />
              Print
            </button>
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {order.status || 'pending'}
            </div>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-gray-100 rounded-full">
            <UserIcon className="h-5 w-5 text-gray-500" />
          </div>
          <h3 className="text-base font-medium text-gray-900">Customer Information</h3>
        </div>
        <div className="space-y-2 pl-11">
          <p className="text-sm">{order.customer?.name}</p>
          <p className="text-sm text-gray-600">{order.customer?.email}</p>
          <p className="text-sm text-gray-600">+{order.customer?.phone}</p>
        </div>
      </div>

      {/* Order Items */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-full">
              <ShoppingBagIcon className="h-5 w-5 text-gray-500" />
            </div>
            <h3 className="text-base font-medium text-gray-900">Order Items</h3>
          </div>
        </div>
        
        {regularItems.length > 0 && (
          <div className="divide-y divide-gray-100">
            {regularItems.map((item, index) => (
              <div key={index} className="p-4 flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  <p className="text-xs text-gray-500 mt-1">Category: {item.category}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    {order.currency} {
                      (parseFloat(item.unit_cost_local_currency || 0) * parseInt(item.quantity || 1, 10)).toFixed(2)
                    }
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.quantity} × {parseFloat(item.unit_cost_local_currency || 0).toFixed(2)} {order.currency}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {giftItems.length > 0 && (
          <>
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-50 rounded-full">
                  <GiftIcon className="h-5 w-5 text-blue-500" />
                </div>
                <h3 className="text-base font-medium text-gray-900">Gift Items</h3>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {giftItems.map((item, index) => (
                <div key={`gift-${index}`} className="p-4 flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.name}</p>
                    <p className="text-xs text-blue-600 mt-1">Gift</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">Free</p>
                    <p className="text-xs text-gray-500">1 × Gift</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Order Summary */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <h3 className="text-base font-medium text-gray-900 mb-4">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Subtotal ({regularItems.length} items)</span>
            <span className="text-sm font-medium">{order.currency} {regularTotal.toFixed(2)}</span>
          </div>
          {giftTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Gifts ({giftItems.length} items)</span>
              <span className="text-sm font-medium text-green-600">Free</span>
            </div>
          )}
          <div className="border-t border-gray-200 my-2"></div>
          <div className="flex justify-between">
            <span className="text-base font-semibold">Total</span>
            <span className="text-base font-bold">
              {order.currency} {parseFloat(order.subtotal_local || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Status */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <h3 className="text-base font-medium text-gray-900 mb-2">Payment Information</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Status</span>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                order.payment?.status === 'paid' || 
                order.paymentMethod === 'cash' || 
                order.status === 'paid' || 
                order.status === 'processing' || 
                order.status === 'processed' || 
                order.paymentStatus === 'paid'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {order.payment?.status === 'paid' || 
                 order.paymentMethod === 'cash' || 
                 order.status === 'paid' || 
                 order.status === 'processing' || 
                 order.status === 'processed' || 
                 order.paymentStatus === 'paid'
                  ? 'Paid'
                  : 'Pending'}
              </span>
            </div>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Method</span>
            <span className="text-sm font-medium">
              {order.payment?.method || (order.paymentMethod === 'cash' ? 'Cash' : 'Online Payment')}
            </span>
          </div>
          {order.payment?.reference && order.payment.reference !== 'CASH PAYMENT' && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Reference</span>
              <span className="text-sm font-mono">{order.payment.reference}</span>
            </div>
          )}
        </div>
        {order.paymentLink && (
          <div className="mt-4">
            <a 
              href={order.paymentLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              Complete Payment
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
